/**
 * Group By configuration for task list views.
 *
 * Defines how tasks can be grouped (e.g., by Records Request status, by matter)
 * and provides human-readable labels for group keys.
 */

import { DataTableGrouping } from '@/components/common/DataTable';
import { Task } from '@/services/types/client-intake-types';

import { GroupByOption } from '../components/GroupBySelector';

// ---------------------------------------------------------------------------
// Records Request Status Display Labels
// ---------------------------------------------------------------------------

/**
 * Human-readable labels for TreatmentRecordRequestStatus values.
 * Maps backend enum values (snake_case) to user-friendly display names.
 */
export const RECORDS_REQUEST_STATUS_LABELS: Record<string, string> = {
  not_ready: 'Not Ready',
  pending: 'Pending Submission',
  request_submitted: 'Request Submitted',
  request_received: 'Confirmed Receipt',
  awaiting_invoice: 'Awaiting Invoice',
  pending_payment: 'Pending Payment',
  records_pending: 'Records Pending',
  records_delivered: 'Records Delivered',
  records_ingested: 'Records Ingested',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  auto_retried: 'Auto Retried',
};

/**
 * Ordering for Records Request statuses (matches backend TREATMENT_RECORD_REQUEST_STATUS_ORDER).
 * Used to sort groups in a logical progression order.
 */
export const RECORDS_REQUEST_STATUS_ORDER: string[] = [
  'not_ready',
  'pending',
  'request_submitted',
  'request_received',
  'awaiting_invoice',
  'pending_payment',
  'records_pending',
  'records_delivered',
  'records_ingested',
  'rejected',
  'cancelled',
  'auto_retried',
];

function getRecordsRequestStatusLabel(status: string): string {
  return RECORDS_REQUEST_STATUS_LABELS[status] || status;
}

function getRecordsRequestStatusOrder(status: string): number {
  const index = RECORDS_REQUEST_STATUS_ORDER.indexOf(status);
  return index === -1 ? RECORDS_REQUEST_STATUS_ORDER.length : index;
}

// ---------------------------------------------------------------------------
// Group Key Extractors
// ---------------------------------------------------------------------------

const NO_RECORDS_REQUEST_KEY = '__no_records_request__';
const NO_RECORDS_REQUEST_LABEL = 'No Record Request';

function getRecordsRequestStatusKey(task: Task): string {
  return task.treatment_record_request?.status || NO_RECORDS_REQUEST_KEY;
}

function getMatterKey(task: Task): string {
  return task.matter || '__no_matter__';
}

function getMatterLabel(task: Task): string {
  return task.matter_name || 'Unknown Matter';
}

function getProviderName(task: Task): string {
  return task.treatment_record_request?.provider_name || 'Unknown Provider';
}

function getRecordsRequestAndMatterKey(task: Task): string {
  return `${getRecordsRequestStatusKey(task)}||${getMatterKey(task)}`;
}

// ---------------------------------------------------------------------------
// Sort comparators for grouped data
// ---------------------------------------------------------------------------

/**
 * Creates a sort comparator that sorts tasks by their group key(s),
 * preserving the original order within each group.
 */
function createGroupSortComparator(
  groupBy: GroupByOption,
): ((a: Task, b: Task) => number) | null {
  switch (groupBy) {
    case 'records_request_status': {
      return (a, b) => {
        const aOrder = getRecordsRequestStatusOrder(getRecordsRequestStatusKey(a));
        const bOrder = getRecordsRequestStatusOrder(getRecordsRequestStatusKey(b));
        return aOrder - bOrder;
      };
    }
    case 'matter': {
      return (a, b) => {
        const aLabel = getMatterLabel(a);
        const bLabel = getMatterLabel(b);
        return aLabel.localeCompare(bLabel);
      };
    }
    case 'records_request_status_and_matter': {
      return (a, b) => {
        // Primary: Records Request status order
        const aOrder = getRecordsRequestStatusOrder(getRecordsRequestStatusKey(a));
        const bOrder = getRecordsRequestStatusOrder(getRecordsRequestStatusKey(b));
        if (aOrder !== bOrder) return aOrder - bOrder;
        // Secondary: matter name alphabetical
        const aLabel = getMatterLabel(a);
        const bLabel = getMatterLabel(b);
        return aLabel.localeCompare(bLabel);
      };
    }
    case 'matter_and_provider': {
      return (a, b) => {
        // Primary: matter name alphabetical
        const aMatter = getMatterLabel(a);
        const bMatter = getMatterLabel(b);
        const matterCmp = aMatter.localeCompare(bMatter);
        if (matterCmp !== 0) return matterCmp;
        // Secondary: provider name alphabetical
        const aProvider = getProviderName(a);
        const bProvider = getProviderName(b);
        return aProvider.localeCompare(bProvider);
      };
    }
    case 'records_request_status_and_matter_and_provider': {
      return (a, b) => {
        // Primary: Records Request status order
        const aOrder = getRecordsRequestStatusOrder(getRecordsRequestStatusKey(a));
        const bOrder = getRecordsRequestStatusOrder(getRecordsRequestStatusKey(b));
        if (aOrder !== bOrder) return aOrder - bOrder;
        // Secondary: matter name alphabetical
        const aMatter = getMatterLabel(a);
        const bMatter = getMatterLabel(b);
        const matterCmp = aMatter.localeCompare(bMatter);
        if (matterCmp !== 0) return matterCmp;
        // Tertiary: provider name alphabetical
        const aProvider = getProviderName(a);
        const bProvider = getProviderName(b);
        return aProvider.localeCompare(bProvider);
      };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a DataTableGrouping config for the given groupBy option.
 * Returns undefined when groupBy is 'none'.
 */
export function getTaskGroupingConfig(
  groupBy: GroupByOption,
): DataTableGrouping<Task> | undefined {
  if (groupBy === 'none') return undefined;

  switch (groupBy) {
    case 'records_request_status':
      return {
        getGroupKey: getRecordsRequestStatusKey,
        getGroupLabel: (key) =>
          key === NO_RECORDS_REQUEST_KEY ? NO_RECORDS_REQUEST_LABEL : getRecordsRequestStatusLabel(key),
      };
    case 'matter':
      return {
        getGroupKey: (task) => getMatterKey(task),
        getGroupLabel: (key) => {
          // The key is the matter ID; we need to return the label.
          // Since we can't look up by ID here, we store matter_name in the key.
          // Let's change approach: use matter_name as key for display grouping.
          return key;
        },
      };
    case 'records_request_status_and_matter':
      return {
        getGroupKey: getRecordsRequestAndMatterKey,
        getGroupLabel: (key) => {
          const [statusKey] = key.split('||');
          const statusLabel =
            statusKey === NO_RECORDS_REQUEST_KEY
              ? NO_RECORDS_REQUEST_LABEL
              : getRecordsRequestStatusLabel(statusKey);
          // Matter label is resolved from the composite key via the lookup below
          return statusLabel;
        },
      };
    default:
      return undefined;
  }
}

/**
 * For "matter" grouping, we use matter_name as key (not UUID) so labels display correctly.
 * For "records_request_status_and_matter", we use "status||matterName" as key.
 *
 * This function returns a grouping config that uses display-friendly keys.
 */
export function getTaskGroupingConfigWithLabels(
  groupBy: GroupByOption,
): DataTableGrouping<Task> | undefined {
  if (groupBy === 'none') return undefined;

  switch (groupBy) {
    case 'records_request_status':
      return {
        getGroupKey: getRecordsRequestStatusKey,
        getGroupLabel: (key) =>
          key === NO_RECORDS_REQUEST_KEY ? NO_RECORDS_REQUEST_LABEL : getRecordsRequestStatusLabel(key),
        getGroupSuffix: (_key, rows) =>
          `${rows.length} ${rows.length === 1 ? 'task' : 'tasks'}`,
      };
    case 'matter':
      return {
        getGroupKey: (task) => getMatterLabel(task),
        getGroupLabel: (key) => key,
        getGroupSuffix: (_key, rows) =>
          `${rows.length} ${rows.length === 1 ? 'task' : 'tasks'}`,
      };
    case 'records_request_status_and_matter':
      return {
        getGroupKey: (task) =>
          `${getRecordsRequestStatusKey(task)}||${getMatterLabel(task)}`,
        getGroupLabel: (key) => {
          const separatorIndex = key.indexOf('||');
          const statusKey = key.substring(0, separatorIndex);
          const matterName = key.substring(separatorIndex + 2);
          const statusLabel =
            statusKey === NO_RECORDS_REQUEST_KEY
              ? NO_RECORDS_REQUEST_LABEL
              : getRecordsRequestStatusLabel(statusKey);
          return `${statusLabel} — ${matterName}`;
        },
        getGroupSuffix: (_key, rows) =>
          `${rows.length} ${rows.length === 1 ? 'task' : 'tasks'}`,
      };
    case 'matter_and_provider':
      return {
        getGroupKey: (task) =>
          `${getMatterLabel(task)}||${getProviderName(task)}`,
        getGroupLabel: (key) => {
          const separatorIndex = key.indexOf('||');
          const matterName = key.substring(0, separatorIndex);
          const providerName = key.substring(separatorIndex + 2);
          return `${matterName} — ${providerName}`;
        },
        getGroupSuffix: (_key, rows) =>
          `${rows.length} ${rows.length === 1 ? 'task' : 'tasks'}`,
      };
    case 'records_request_status_and_matter_and_provider':
      return {
        getGroupKey: (task) =>
          `${getRecordsRequestStatusKey(task)}||${getMatterLabel(task)}||${getProviderName(task)}`,
        getGroupLabel: (key) => {
          const parts = key.split('||');
          const statusKey = parts[0];
          const matterName = parts[1] || '';
          const providerName = parts[2] || '';
          const statusLabel =
            statusKey === NO_RECORDS_REQUEST_KEY
              ? NO_RECORDS_REQUEST_LABEL
              : getRecordsRequestStatusLabel(statusKey);
          return `${statusLabel} — ${matterName} — ${providerName}`;
        },
        getGroupSuffix: (_key, rows) =>
          `${rows.length} ${rows.length === 1 ? 'task' : 'tasks'}`,
      };
    default:
      return undefined;
  }
}

/**
 * Sort tasks for grouped display. Returns a new sorted array.
 * When groupBy is 'none', returns the original array unchanged.
 */
export function sortTasksForGrouping(
  tasks: Task[],
  groupBy: GroupByOption,
): Task[] {
  const comparator = createGroupSortComparator(groupBy);
  if (!comparator) return tasks;
  return [...tasks].sort(comparator);
}
